import Reveal from "@/components/base/Reveal";
import { whatsappLink } from "@/constants/site";

export interface PlanSpec {
  label: string;
  value: string;
}

export interface PlanCardItem {
  name: string;
  audience: string;
  price: string;
  unit?: string;
  badge?: string;
  badgeTone?: "accent" | "primary";
  highlighted?: boolean;
  specs?: PlanSpec[];
  features: string[];
}

interface PlanCardsProps {
  plans: PlanCardItem[];
  columns?: 3 | 4;
  ctaMessage?: string;
  ctaLabel?: string;
  note?: string;
}

const COLUMN_CLASS: Record<number, string> = {
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
};

const BADGE_TONE_CLASS: Record<string, string> = {
  accent: "bg-accent-500 text-background-50",
  primary: "bg-primary-500 text-background-50",
};

export default function PlanCards({
  plans,
  columns = 3,
  ctaMessage = "Ola! Tenho interesse no plano",
  ctaLabel = "Contratar agora",
  note,
}: PlanCardsProps) {
  return (
    <>
      <div className={`grid grid-cols-1 gap-6 ${COLUMN_CLASS[columns]}`}>
        {plans.map((plan, index) => (
          <Reveal key={plan.name} delay={index * 70} className="h-full">
            <article
              className={`relative flex h-full flex-col rounded-xl border bg-background-50 p-6 transition-colors ${
                plan.highlighted
                  ? "border-primary-300"
                  : "border-background-200 hover:border-primary-200"
              }`}
            >
              {plan.badge ? (
                <span
                  className={`absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                    BADGE_TONE_CLASS[plan.badgeTone ?? "accent"]
                  }`}
                >
                  <i className="ri-star-line" aria-hidden="true"></i>
                  {plan.badge}
                </span>
              ) : null}

              <h3 className="font-heading text-lg font-semibold text-foreground-950">{plan.name}</h3>
              <p className="mt-2 min-h-[40px] text-sm text-foreground-600">{plan.audience}</p>

              <div className="mt-5 flex items-end gap-1">
                <span className="text-sm font-medium text-foreground-500">R$</span>
                <span className="font-heading text-4xl font-bold text-foreground-950">
                  {plan.price}
                </span>
                <span className="mb-1 text-sm text-foreground-500">{plan.unit ?? "/mês"}</span>
              </div>

              {plan.specs && plan.specs.length > 0 ? (
                <dl className="mt-5 grid grid-cols-2 gap-2 rounded-lg bg-background-100 p-3">
                  {plan.specs.map((spec) => (
                    <div key={spec.label}>
                      <dt className="text-[11px] uppercase tracking-wide text-foreground-500">
                        {spec.label}
                      </dt>
                      <dd className="text-sm font-semibold text-foreground-900">{spec.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}

              <ul className="mt-6 flex flex-1 flex-col gap-3">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-check-line mt-0.5 text-secondary-600" aria-hidden="true"></i>
                    {feature}
                  </li>
                ))}
              </ul>

              <a
                href={whatsappLink(`${ctaMessage} ${plan.name} da NXHOST.`)}
                target="_blank"
                rel="noopener noreferrer"
                className={`mt-7 inline-flex items-center justify-center whitespace-nowrap rounded-md px-4 py-3 text-sm font-semibold transition-colors ${
                  plan.highlighted
                    ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                    : "bg-foreground-950 text-background-50 hover:bg-foreground-800"
                }`}
              >
                {ctaLabel}
              </a>
            </article>
          </Reveal>
        ))}
      </div>

      {note ? (
        <Reveal>
          <p className="mt-6 text-center text-xs text-foreground-500">{note}</p>
        </Reveal>
      ) : null}
    </>
  );
}